/**
 * Le langage des conditions, côté éditeur — le double de ConditionExpression (Ruby) : même
 * grammaire, même sémantique, mêmes cas (test/fixtures/files/condition_cases.json). Le code
 * est la condition : c'est lui qui est enregistré (`bindings.visible.expr`), et c'est lui que
 * le serveur évalue à l'expansion.
 *
 *   $NBR_PIECES > 4 || $BILAN_CONSOMMATION == "A"
 *   ($SURFACE && $SURFACE > 0) && !($PRIX > 500000)
 *
 * Ici, en plus de l'évaluation (le fantôme à 22 % du calque masqué pour le sujet d'aperçu,
 * le point vert d'une règle) : la lecture du code en CARTES (`toBlocks`) quand la forme s'y
 * prête, et sa réécriture quand on touche aux cartes (`fromBlocks`). Une carte = des règles
 * reliées par ET ou par OU, et une action (afficher si / masquer si) ; plusieurs cartes sont
 * indépendantes, reliées par `&&`. Une forme que les cartes ne savent pas montrer reste
 * valable, modifiable en code.
 *
 * Les chaînes d'opérations d'une variable (`$X.downcase`, `.split`…) passent par la lib
 * variable_chain, la même que l'interpolation des textes.
 */

import { chained, lookup, VARIABLE_SOURCE } from "./variable_chain";

export type Cmp = "==" | "!=" | ">" | "<" | ">=" | "<=";
export type Node =
  | { t: "var"; name: string; ops: string; paren?: boolean }
  | { t: "lit"; v: string; num: boolean; paren?: boolean }
  | { t: "not"; a: Node; paren?: boolean }
  | { t: "and" | "or"; list: Node[]; paren?: boolean }
  | { t: "cmp"; op: Cmp; a: Node; b: Node; paren?: boolean };

type Tok = { k: "var" | "str" | "num" | "word" | "op" | "(" | ")"; v: string; at: number; ops?: string };

export class ConditionError extends Error {
  at: number;
  constructor(message: string, at: number) {
    super(message);
    this.at = at;
  }
}

export type Op = "filled" | "empty" | "eq" | "neq" | "gt" | "lt" | "gte" | "lte";
export interface Rule {
  key: string;
  op: Op;
  value?: string;
}
export interface Block {
  join: "and" | "or";
  rules: Rule[];
  /** afficher si les règles passent, ou masquer si elles passent */
  action: "show" | "hide";
}

/** `label` : court, pour la liste ; `sym` : le code. */
export const OPS: { op: Op; label: string; sym: string; needsValue: boolean }[] = [
  { op: "filled", label: "est remplie", sym: "", needsValue: false },
  { op: "empty", label: "est vide", sym: "!", needsValue: false },
  { op: "eq", label: "=", sym: "==", needsValue: true },
  { op: "neq", label: "≠", sym: "!=", needsValue: true },
  { op: "gt", label: ">", sym: ">", needsValue: true },
  { op: "lt", label: "<", sym: "<", needsValue: true },
  { op: "gte", label: "≥", sym: ">=", needsValue: true },
  { op: "lte", label: "≤", sym: "<=", needsValue: true },
];

/* ---------- lecture du code ---------- */

const COMPARATORS: Cmp[] = ["==", "!=", ">=", "<=", ">", "<"];
const QUOTES: Record<string, string> = { '"': '"', "'": "'", "“": "”", "«": "»" };
const VARIABLE = new RegExp(`^${VARIABLE_SOURCE}`);
const WORD = /^[^\s()&|=!<>"'“«$]+/;
const NUMBER_LITERAL = /^-?\d+([.,]\d+)?$/;

function tokenize(src: string): Tok[] {
  const out: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const rest = src.slice(i);
    const c = src[i];
    let m: RegExpExecArray | null;
    if (/\s/.test(c)) {
      i++;
    } else if (c === "(" || c === ")") {
      out.push({ k: c, v: c, at: i++ });
    } else if ((m = /^(&&|\|\||==|!=|>=|<=|[!<>])/.exec(rest))) {
      out.push({ k: "op", v: m[0], at: i });
      i += m[0].length;
    } else if (c === "=") {
      // « = » seul se lit comme « == » : on tolère ce qu'on tape à la main
      out.push({ k: "op", v: "==", at: i++ });
    } else if (c === "$") {
      if (!(m = VARIABLE.exec(rest))) throw new ConditionError("« $ » doit être suivi d'un nom de variable", i);
      out.push({ k: "var", v: m[1], ops: m[2], at: i });
      i += m[0].length;
    } else if ((m = /^\\?(["'“«])/.exec(rest))) {
      const close = QUOTES[m[1]];
      const body = rest.slice(m[0].length);
      const j = body.indexOf(close);
      if (j < 0) throw new ConditionError("Guillemet non fermé", i);
      // un guillemet échappé (\") ferme aussi : on retire la barre
      out.push({ k: "str", v: body.slice(0, j).replace(/\\$/, "").trim(), at: i });
      i += m[0].length + j + 1;
    } else if ((m = WORD.exec(rest))) {
      out.push({ k: NUMBER_LITERAL.test(m[0]) ? "num" : "word", v: m[0], at: i });
      i += m[0].length;
    } else {
      throw new ConditionError(`Caractère inattendu « ${c} »`, i);
    }
  }
  return out;
}

/** L'arbre d'un code, null pour un code vide. Lève une ConditionError si le code ne se lit pas. */
export function parseCondition(src: string): Node | null {
  const toks = tokenize(src);
  if (!toks.length) return null;
  let p = 0;
  const peek = () => toks[p];
  const eat = (v?: string) => {
    const t = toks[p];
    if (!t || (v && t.v !== v)) throw new ConditionError(v ? `« ${v} » attendu` : "Expression incomplète", t?.at ?? src.length);
    p++;
    return t;
  };
  const list = (kind: "and" | "or", items: Node[]): Node => (items.length === 1 ? items[0] : { t: kind, list: items });
  const or = (): Node => {
    const items = [and()];
    while (peek()?.v === "||") {
      eat();
      items.push(and());
    }
    return list("or", items);
  };
  const and = (): Node => {
    const items = [not()];
    while (peek()?.v === "&&") {
      eat();
      items.push(not());
    }
    return list("and", items);
  };
  const not = (): Node => {
    if (peek()?.v === "!") {
      eat();
      return { t: "not", a: not() };
    }
    return cmp();
  };
  // Un mot nu seul (`invalid`, `prix`) n'est pas une condition : c'est un `$` oublié — en
  // comparaison, en revanche, il vaut un littéral (`$STATUS == a_vendre`)
  const cmp = (): Node => {
    const first = peek();
    const a = primary();
    const t = peek();
    if (t?.k === "op" && (COMPARATORS as string[]).includes(t.v)) {
      eat();
      return { t: "cmp", op: t.v as Cmp, a, b: primary() };
    }
    if (first.k === "word") throw new ConditionError(`« ${first.v} » n'est pas une variable ($${first.v.toUpperCase()} ?)`, first.at);
    return a;
  };
  const primary = (): Node => {
    const t = peek();
    if (!t) throw new ConditionError("Expression incomplète", src.length);
    if (t.k === "(") {
      eat("(");
      const n = or();
      eat(")");
      return { ...n, paren: true };
    }
    eat();
    if (t.k === "var") return { t: "var", name: t.v, ops: t.ops ?? "" };
    if (t.k === "num") return { t: "lit", v: t.v.replace(",", "."), num: true };
    if (t.k === "str" || t.k === "word") return { t: "lit", v: t.v, num: false };
    throw new ConditionError(`« ${t.v} » inattendu`, t.at);
  };
  const n = or();
  if (p < toks.length) throw new ConditionError(`« ${toks[p].v} » en trop`, toks[p].at);
  return n;
}

/** Le message d'erreur d'un code, null s'il se lit. */
export function checkCondition(code: string): string | null {
  try {
    parseCondition(code);
    return null;
  } catch (e) {
    return e instanceof ConditionError ? e.message : "Code illisible";
  }
}

/* ---------- évaluation ---------- */

/** Le premier nombre d'une valeur : « 4 pièces · 3 chambres » → 4, « 320 000 € » → 320000, « 82,5 m² » → 82,5. */
export function numberIn(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const m = /-?\d{1,3}(?:[   .]\d{3})+(?:,\d+)?|-?\d+(?:[.,]\d+)?/.exec(String(value ?? ""));
  if (!m) return null;
  const s = m[0].replace(/[   ]/g, "");
  // virgule = décimale (les points sont des milliers) ; sinon « .000 » sépare des milliers
  const n = Number(s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : /\.\d{3}$/.test(s) ? s.replace(/\./g, "") : s);
  return Number.isFinite(n) ? n : null;
}

type Value = string | number | null;

/** Vide, 0 et false ne passent pas (la règle de StringInterpolator#absent?) : null pour absente. */
function present(value: unknown): Value {
  if (value === null || value === undefined) return null;
  if (typeof value === "boolean") return value ? "true" : null;
  if (typeof value === "number") return value === 0 ? null : value;
  const s = String(value).trim();
  if (s === "") return null;
  return Number(s) === 0 ? null : s;
}

/** La valeur d'une variable pour ces valeurs : le nom ne dépend pas de la casse, la chaîne
 *  d'opérations s'applique (lib variable_chain) et son résultat vide vaut absent. */
function resolve(values: Record<string, unknown>, name: string, ops: string): Value {
  const value = present(lookup(values, name));
  if (value === null || !ops) return value;
  return present(chained(String(value), ops));
}

export function evaluateNode(n: Node, values: Record<string, unknown>): boolean {
  const val = (x: Node): Value => (x.t === "var" ? resolve(values, x.name, x.ops) : x.t === "lit" ? x.v : evaluateNode(x, values) ? "1" : null);
  switch (n.t) {
    case "var":
      return resolve(values, n.name, n.ops) !== null;
    case "lit":
      return n.num ? Number(n.v) !== 0 : n.v !== "";
    case "not":
      return !evaluateNode(n.a, values);
    case "and":
      return n.list.every((x) => evaluateNode(x, values));
    case "or":
      return n.list.some((x) => evaluateNode(x, values));
    case "cmp": {
      const a = val(n.a);
      const b = val(n.b);
      if (n.op === "==" || n.op === "!=") {
        // numérique dès qu'un littéral est un nombre, ou que les deux valeurs en sont
        const numeric = (n.a.t === "lit" && n.a.num) || (n.b.t === "lit" && n.b.num) ||
          (typeof a === "number" && typeof b === "number");
        const na = numberIn(a);
        const same = numeric ? na !== null && na === numberIn(b) : String(a ?? "") === String(b ?? "");
        return n.op === "==" ? same : !same;
      }
      // une valeur vide n'est ni plus grande ni plus petite que rien
      if (a === null || b === null) return false;
      const na = numberIn(a);
      const nb = numberIn(b);
      const d = na !== null && nb !== null ? Math.sign(na - nb) : String(a) < String(b) ? -1 : String(a) > String(b) ? 1 : 0;
      return n.op === ">" ? d > 0 : n.op === "<" ? d < 0 : n.op === ">=" ? d >= 0 : d <= 0;
    }
  }
}

/** La condition passe-t-elle pour ces valeurs ? Un code vide passe ; un code illisible ne
 *  passe pas — comme au serveur. */
export function evaluateCondition(code: string, values: Record<string, unknown>): boolean {
  try {
    const n = parseCondition(code);
    return n ? evaluateNode(n, values) : true;
  } catch {
    return false;
  }
}

/** Les variables lues par un code, en majuscules — rien pour un code illisible. */
export function conditionTokens(code: string): string[] {
  const out = new Set<string>();
  const walk = (n: Node) => {
    if (n.t === "var") out.add(n.name.toUpperCase());
    else if (n.t === "not") walk(n.a);
    else if (n.t === "and" || n.t === "or") n.list.forEach(walk);
    else if (n.t === "cmp") {
      walk(n.a);
      walk(n.b);
    }
  };
  try {
    const n = parseCondition(code);
    if (n) walk(n);
  } catch {
    /* code illisible : aucune variable */
  }
  return [...out];
}

/* ---------- écriture du code ---------- */

const lit = (v: string) => (NUMBER_LITERAL.test(v.trim()) ? v.trim().replace(",", ".") : `"${v.replace(/"/g, "'")}"`);

export function printNode(n: Node, top = true): string {
  const wrap = (s: string) => (top ? s : `(${s})`);
  switch (n.t) {
    case "var":
      return `$${n.name}${n.ops}`;
    case "lit":
      return n.num ? n.v : lit(n.v);
    case "not":
      return n.a.t === "var" ? `!${printNode(n.a)}` : `!(${printNode(n.a, true)})`;
    case "cmp":
      return `${printNode(n.a, false)} ${n.op} ${printNode(n.b, false)}`;
    case "and":
    case "or":
      return wrap(n.list.map((x) => printNode(x, x.t !== "and" && x.t !== "or")).join(n.t === "and" ? " && " : " || "));
  }
}

const ruleCode = (r: Rule) => {
  const v = `$${r.key}`;
  if (r.op === "filled") return v;
  if (r.op === "empty") return `!${v}`;
  return `${v} ${OPS.find((o) => o.op === r.op)!.sym} ${lit(r.value ?? "")}`;
};

/** Les cartes → le code. Plusieurs cartes : chacune entre parenthèses, reliées par &&. */
export function fromBlocks(blocks: Block[]): string {
  const live = blocks.filter((b) => b.rules.length);
  const many = live.length > 1;
  return live
    .map((b) => {
      const inner = b.rules.map(ruleCode).join(b.join === "and" ? " && " : " || ");
      if (b.action === "hide") return `!(${inner})`;
      return many ? `(${inner})` : inner;
    })
    .join(" && ");
}

/* ---------- le code → les cartes (quand la forme s'y prête) ---------- */

const flip: Record<Cmp, Cmp> = { "==": "==", "!=": "!=", ">": "<", "<": ">", ">=": "<=", "<=": ">=" };
const OP_OF: Record<Cmp, Op> = { "==": "eq", "!=": "neq", ">": "gt", "<": "lt", ">=": "gte", "<=": "lte" };

/** Une variable nue (sans chaîne d'opérations) : seule une carte sait la montrer. */
const plainVar = (n: Node): n is Extract<Node, { t: "var" }> => n.t === "var" && !n.ops;

function atom(n: Node): Rule | null {
  if (plainVar(n)) return { key: n.name, op: "filled" };
  if (n.t === "not" && plainVar(n.a) && !n.a.paren) return { key: n.a.name, op: "empty" };
  if (n.t === "cmp") {
    if (plainVar(n.a) && n.b.t === "lit") return { key: n.a.name, op: OP_OF[n.op], value: n.b.v };
    if (n.a.t === "lit" && plainVar(n.b)) return { key: n.b.name, op: OP_OF[flip[n.op]], value: n.a.v };
  }
  return null;
}

/** une liste de règles reliées par un seul lien (ET ou OU), ou une règle seule */
function group(n: Node): { join: "and" | "or"; rules: Rule[] } | null {
  const a = atom(n);
  if (a) return { join: "and", rules: [a] };
  if (n.t === "and" || n.t === "or") {
    const rules = n.list.map((x) => (x.paren ? null : atom(x)));
    return rules.every(Boolean) ? { join: n.t, rules: rules as Rule[] } : null;
  }
  return null;
}

/** Les cartes d'un code : [] pour un code vide, null quand les cartes ne savent pas le montrer
 *  (forme mêlant ET et OU, chaîne d'opérations, code illisible). */
export function toBlocks(code: string): Block[] | null {
  let n: Node | null;
  try {
    n = parseCondition(code);
  } catch {
    return null;
  }
  if (!n) return [];
  // un OU au sommet : une seule carte
  if (n.t === "or" && !n.paren) {
    const g = group(n);
    return g ? [{ ...g, action: "show" }] : null;
  }
  const terms = n.t === "and" && !n.paren ? n.list : [n];
  const out: Block[] = [];
  let loose: Rule[] = [];
  const flush = () => {
    if (loose.length) out.push({ join: "and", rules: loose, action: "show" });
    loose = [];
  };
  for (const term of terms) {
    if (term.t === "not" && (term.a.paren || !plainVar(term.a))) {
      const g = group(term.a);
      if (!g) return null;
      flush();
      out.push({ ...g, action: "hide" });
      continue;
    }
    if (term.paren) {
      const g = group(term);
      if (!g) return null;
      flush();
      out.push({ ...g, action: "show" });
      continue;
    }
    const a = atom(term);
    if (!a) return null;
    loose.push(a);
  }
  flush();
  return out;
}

/* ---------- lire une condition en français ---------- */

export type Labeler = (key: string) => string;
const asIs: Labeler = (key) => key;

export const ruleLabel = (r: Rule, label: Labeler = asIs) => {
  const op = OPS.find((o) => o.op === r.op)!;
  return op.needsValue ? `${label(r.key)} ${op.label} ${r.value ?? ""}` : `${label(r.key)} ${op.label}`;
};
export const blockLabel = (b: Block, label: Labeler = asIs) => b.rules.map((r) => ruleLabel(r, label)).join(b.join === "and" ? " et " : " ou ");

/** La condition, lue : « Pièces > 4 ou Classe énergie = A ». Une forme libre se lit en code. */
export function conditionLabel(code: string, label: Labeler = asIs): string {
  const blocks = toBlocks(code);
  if (!blocks) return code;
  return blocks
    .map((b) => (b.action === "hide" ? `pas (${blockLabel(b, label)})` : blocks.length > 1 && b.rules.length > 1 ? `(${blockLabel(b, label)})` : blockLabel(b, label)))
    .join(" et ");
}

/** Un collage : « ?{($NBR_PIECES > 4) texte} » ou « ($X) » → la condition seule. */
export function extractCondition(pasted: string): string {
  const s = pasted.trim();
  const m = /^\?\{\s*\(/.exec(s);
  if (m) {
    let depth = 0;
    for (let i = m[0].length - 1; i < s.length; i++) {
      if (s[i] === "(") depth++;
      else if (s[i] === ")" && --depth === 0) return s.slice(m[0].length, i).trim();
    }
  }
  return s;
}
